import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Dashboard } from '@etnrlz/shared';
import { api } from '../../lib/api';
import { config } from '../../config';
import { Icon } from '../../components/icons';
import { osStatusMeta } from '../../components/status';
import { pluralDays } from '../../lib/format';

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function KioskPage() {
  const now = useClock();
  const { data, isError, dataUpdatedAt } = useQuery({
    queryKey: ['kiosk'],
    queryFn: () => api.get<Dashboard>('/kiosk', { kioskToken: config.kioskToken }),
    refetchInterval: 30_000,
  });

  return (
    <div className="min-h-screen bg-bg text-text p-6 lg:p-10 select-none">
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Icon name="flag" className="h-10 w-10 text-accent" />
          <div>
            <div className="text-3xl font-bold tracking-tight">
              ETNRLZ <span className="text-accent">Garage</span>
            </div>
            <div className="text-muted text-sm flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-st-done animate-pulse-dot" />
              ao vivo · atualizado {new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="num text-5xl font-bold tracking-tight">
            {now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-muted">
            {now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
          </div>
        </div>
      </header>

      {isError && (
        <div className="card p-4 border-st-danger/40 text-st-danger mb-6">
          Erro ao carregar dados do kiosk. Verifique o token.
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[
              { label: 'OS abertas', value: data.kpis.osAbertas, accent: true },
              { label: 'Carros na garagem', value: data.kpis.carrosNaGaragem },
              { label: 'Peças abaixo do mín.', value: data.kpis.pecasAbaixoMinimo },
              { label: 'Vencimentos', value: data.kpis.vencimentosProximos },
            ].map((k) => (
              <div key={k.label} className="card p-5">
                <div className="text-muted uppercase tracking-wide text-sm">{k.label}</div>
                <div className={`num text-6xl font-bold mt-1 ${k.accent ? 'text-accent' : ''}`}>
                  {k.value}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-6">
            {/* Ordens abertas */}
            <div className="col-span-2">
              <h2 className="text-xl font-semibold mb-3 text-muted uppercase tracking-wide">
                Ordens em aberto
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {data.osAbertas.slice(0, 8).map((o) => {
                  const meta = osStatusMeta[o.status];
                  return (
                    <div
                      key={o.id}
                      className="card p-4 border-l-4"
                      style={{ borderLeftColor: meta.color }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-lg leading-tight">{o.titulo}</div>
                        <span className="num text-2xl font-bold text-accent shrink-0">{o.diasParado}d</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: meta.color }}>
                        <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
                        {meta.label}
                      </div>
                    </div>
                  );
                })}
                {data.osAbertas.length === 0 && (
                  <div className="text-muted col-span-2 py-10 text-center text-xl">
                    Nenhuma OS aberta 🏁
                  </div>
                )}
              </div>
            </div>

            {/* Carros parados + alertas */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-3 text-muted uppercase tracking-wide">
                  Parados há mais tempo
                </h2>
                <div className="space-y-2">
                  {data.carrosParados.slice(0, 5).map((c, i) => (
                    <div key={c.vehicleId} className="card p-3 flex items-center gap-3">
                      <span className="num text-2xl font-bold text-muted w-7">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{c.apelido}</div>
                        <div className="text-xs text-muted truncate">{c.modelo}</div>
                      </div>
                      <div className="text-right">
                        <div className="num text-2xl font-bold text-accent">{c.diasParado}</div>
                        <div className="text-[11px] text-muted">{pluralDays(c.diasParado)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-3 text-muted uppercase tracking-wide flex items-center gap-2">
                  <Icon name="alert" className="h-5 w-5 text-st-warn" /> Alertas
                </h2>
                <div className="space-y-2">
                  {[...data.alertasVencimento, ...data.preventivasVencendo.map((p) => ({
                    apelido: p.apelido,
                    label: p.titulo,
                    status: p.status,
                    diasRestantes: undefined as number | undefined,
                  }))]
                    .slice(0, 6)
                    .map((a, i) => (
                      <div key={i} className="card p-3 flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{a.label}</div>
                          <div className="text-xs text-muted truncate">{a.apelido}</div>
                        </div>
                        <span
                          className="text-sm font-semibold"
                          style={{ color: a.status === 'VENCIDO' || a.status === 'VENCIDA' ? '#ef4444' : '#f59e0b' }}
                        >
                          {a.status}
                        </span>
                      </div>
                    ))}
                  {data.alertasVencimento.length === 0 && data.preventivasVencendo.length === 0 && (
                    <div className="text-muted text-center py-6">Tudo em dia ✓</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
